import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { useCurrentFarm } from "model/useFarmQueries";
import { useMemo } from "react";
import { getFeedBudgetPlan, listFeedBudgetPlans } from "services";

function getPlanMonthIndex(planStartDate: string): number {
  const start = new Date(planStartDate);
  const now = new Date();
  return (now.getMonth() - start.getMonth() + 12) % 12;
}

function computeGroupDemandKgDay(
  agb: {
    stockClassPlans: Array<{
      months: Array<{
        month: number;
        animalCount: number;
        averageWeightKg: number;
        allocationPercent: number;
      }>;
    }>;
  },
  month: number
): number {
  let demand = 0;
  for (const sc of agb.stockClassPlans) {
    const entry = sc.months.find((m) => m.month === month);
    if (entry) {
      demand +=
        ((entry.averageWeightKg * entry.allocationPercent) / 100) *
        entry.animalCount;
    }
  }
  return demand;
}

/**
 * Returns a Map<animalGroupId, demandKgDay> derived from the active feed budget
 * plan for the current plan month. Returns an empty map if no active plan exists,
 * so callers can fall back to backend stock class demand.
 */
export function useActiveFeedBudgetDemand(): Map<string, number> {
  const { data: farm } = useCurrentFarm();
  const farmGuid = farm?.guid;

  const { data: planList } = useQuery({
    queryKey: farmGuid
      ? queryKeys.feedBudgetPlans.list(farmGuid)
      : ["feedBudgetPlans", "list", ""],
    queryFn: async () => {
      if (!farmGuid) throw new Error("Farm GUID required");
      return listFeedBudgetPlans(farmGuid);
    },
    enabled: !!farmGuid,
    staleTime: 5 * 60 * 1000,
  });

  const activePlan = planList?.find((p) => p.isActive) ?? null;
  const activePlanId = activePlan?.guid ?? null;
  const activePlanStartDate = activePlan?.startDate ?? null;

  const { data: fullPlan } = useQuery({
    queryKey: activePlanId
      ? queryKeys.feedBudgetPlans.detail(activePlanId)
      : ["feedBudgetPlans", "detail", ""],
    queryFn: async () => {
      if (!activePlanId) throw new Error("Plan ID required");
      return getFeedBudgetPlan(activePlanId);
    },
    enabled: !!activePlanId,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    if (!fullPlan || !activePlanStartDate) return new Map<string, number>();
    const month = getPlanMonthIndex(activePlanStartDate);
    const result = new Map<string, number>();
    for (const agb of fullPlan.animalGroupBudgets) {
      result.set(agb.animalGroupId, computeGroupDemandKgDay(agb, month));
    }
    return result;
  }, [fullPlan, activePlanStartDate]);
}
